import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Bar, Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import DatePicker from '../DatePicker';
import ChartCard from '../charts/ChartCard';
import { ChatDashboardTabProps } from '../../types';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  getDisciplineDistribution,
  getIndustryDistribution,
  getTopicsDistribution,
  TopicDistributionItem,
  DisciplineDistributionItem,
  IndustryDistributionItem,
} from '../../utils/dashboardApi';
import {
  buildTopicsDataset,
  TopicsDataset,
} from '../../utils/topicsDistribution';
import {
  buildDisciplinesDataset,
  DisciplinesDataset,
} from '../../utils/disciplinesDistribution';
import {
  buildIndustriesDataset,
  IndustriesDataset,
} from '../../utils/industriesDistribution';
import SearchDialog from '../SearchDialog';
import { buildCsvContent, downloadCsv } from '../../utils/exportUtils';
import {
  buildTopicsCsvSpec,
  buildWordCloudCsvSpec,
  buildDisciplinesCsvSpec,
  buildIndustriesCsvSpec,
} from '../../utils/exportConfigs';
import { DASHBOARD_CARD_TOOLTIPS } from '../../utils/tooltips';

const TOP_TOPICS_DISPLAY_LIMIT = 5;
const TOP_TOPICS_REQUEST_LIMIT = 17;
const DISCIPLINE_CHART_DISPLAY_LIMIT = 5;
const DISCIPLINE_REQUEST_LIMIT = 999999;
const INDUSTRY_CHART_DISPLAY_LIMIT = 5;
const INDUSTRY_REQUEST_LIMIT = 999999;
const WORD_CLOUD_TOPICS_LIMIT = 17;
const TOPIC_BAR_COLOR = '#3b82f6';
const TOPIC_NEUTRAL_COLOR = '#94a3b8';
const WORD_CLOUD_FONT_MIN = 6;
const WORD_CLOUD_FONT_MAX = 20;
const WORD_CLOUD_WEIGHT_MIN = 6;
const WORD_CLOUD_WEIGHT_MAX = 20;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTooltip,
  Legend,
  WordCloudController,
  WordElement
);

class WordCloudErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    console.error('WordCloud render error:', err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          Word cloud unavailable.
        </div>
      );
    }
    return this.props.children;
  }
}

const TopicsTab: React.FC<ChatDashboardTabProps> = ({ dateRange, onDateRangeChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [topTopicsData, setTopTopicsData] = useState<TopicsDataset>({ labels: [], values: [], colors: [], sdgNumbers: [] });
  const [topTopicsLoading, setTopTopicsLoading] = useState(false);
  const [topTopicsError, setTopTopicsError] = useState<string | null>(null);
  const [disciplinesData, setDisciplinesData] = useState<DisciplinesDataset>({ labels: [], values: [] });
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesError, setDisciplinesError] = useState<string | null>(null);
  const [industriesData, setIndustriesData] = useState<IndustriesDataset>({ labels: [], values: [] });
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industriesError, setIndustriesError] = useState<string | null>(null);
  const [wordCloudTopicsData, setWordCloudTopicsData] = useState<TopicsDataset>({ labels: [], values: [], colors: [], sdgNumbers: [] });
  const [wordCloudLoading, setWordCloudLoading] = useState(false);
  const [wordCloudError, setWordCloudError] = useState<string | null>(null);
  const [rawTopTopics, setRawTopTopics] = useState<TopicDistributionItem[] | null>(null);
  const [rawDisciplines, setRawDisciplines] = useState<DisciplineDistributionItem[]>([]);
  const [rawIndustries, setRawIndustries] = useState<IndustryDistributionItem[]>([]);
  const [rawWordCloudTopics, setRawWordCloudTopics] = useState<TopicDistributionItem[]>([]);

  // Ref and state for word cloud container dimensions
  const wordCloudContainerRef = useRef<HTMLDivElement>(null);
  const [wordCloudDimensions, setWordCloudDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setTopTopicsData({ labels: [], values: [], colors: [], sdgNumbers: [] });
      setTopTopicsLoading(false);
      setTopTopicsError('Please select a valid time range.');
      setRawTopTopics(null);
      setDisciplinesData({ labels: [], values: [] });
      setDisciplinesLoading(false);
      setDisciplinesError('Please select a valid time range.');
      setRawDisciplines([]);
      setIndustriesData({ labels: [], values: [] });
      setIndustriesLoading(false);
      setIndustriesError('Please select a valid time range.');
      setRawIndustries([]);
      setWordCloudTopicsData({ labels: [], values: [], colors: [], sdgNumbers: [] });
      setWordCloudLoading(false);
      setWordCloudError('Please select a valid time range.');
      setRawWordCloudTopics([]);
      setWordCloudDimensions(null); // Reset dimensions when date range changes
      return;
    }

    // Reset dimensions when fetching new data
    setWordCloudDimensions(null);

    let isMounted = true;
    const controller = new AbortController();

    const fetchTopTopics = async () => {
      setTopTopicsLoading(true);
      setTopTopicsError(null);

      try {
        const response = await getTopicsDistribution({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: TOP_TOPICS_REQUEST_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const sortedTopics = [...(response.data.topics ?? [])].sort(
          (a, b) => (b.message_count ?? 0) - (a.message_count ?? 0)
        );

        const topTopics = sortedTopics.slice(0, TOP_TOPICS_DISPLAY_LIMIT);
        const extraTopicsCount = sortedTopics
          .slice(TOP_TOPICS_DISPLAY_LIMIT)
          .reduce((sum, topic) => sum + (topic.message_count ?? 0), 0);
        const apiOthersCount = response.data.others?.message_count ?? 0;
        const combinedOthersCount = extraTopicsCount + apiOthersCount;

        const dataset = buildTopicsDataset(topTopics, combinedOthersCount);
        setTopTopicsData(dataset);
        setRawTopTopics(sortedTopics);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load top topics distribution:', error);
        if (isMounted) {
          setTopTopicsError('Failed to load top topics data.');
          setRawTopTopics(null);
        }
      } finally {
        if (isMounted) {
          setTopTopicsLoading(false);
        }
      }
    };

    const fetchWordCloudTopics = async () => {
      setWordCloudLoading(true);
      setWordCloudError(null);

      try {
        const response = await getTopicsDistribution({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: WORD_CLOUD_TOPICS_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const dataset = buildTopicsDataset(
          response.data.topics,
          response.data.others?.message_count ?? 0
        );
        setWordCloudTopicsData(dataset);
        setRawWordCloudTopics(response.data.topics ?? []);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load word cloud topics:', error);
        if (isMounted) {
          setWordCloudError('Failed to load word cloud data.');
          setRawWordCloudTopics([]);
        }
      } finally {
        if (isMounted) {
          setWordCloudLoading(false);
        }
      }
    };

    const fetchDisciplines = async () => {
      setDisciplinesLoading(true);
      setDisciplinesError(null);

      try {
        const response = await getDisciplineDistribution({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: DISCIPLINE_REQUEST_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const sortedDisciplines = [...(response.data.disciplines ?? [])].sort(
          (a, b) => (b.message_count ?? 0) - (a.message_count ?? 0)
        );
        const chartDisciplines = sortedDisciplines.slice(0, DISCIPLINE_CHART_DISPLAY_LIMIT);
        const dataset = buildDisciplinesDataset(chartDisciplines);
        setDisciplinesData(dataset);
        setRawDisciplines(sortedDisciplines);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load disciplines distribution:', error);
        if (isMounted) {
          setDisciplinesError('Failed to load disciplines data.');
          setRawDisciplines([]);
        }
      } finally {
        if (isMounted) {
          setDisciplinesLoading(false);
        }
      }
    };

    const fetchIndustries = async () => {
      setIndustriesLoading(true);
      setIndustriesError(null);

      try {
        const response = await getIndustryDistribution({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: INDUSTRY_REQUEST_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const sortedIndustries = [...(response.data.industries ?? [])].sort(
          (a, b) => (b.message_count ?? 0) - (a.message_count ?? 0)
        );
        const chartIndustries = sortedIndustries.slice(0, INDUSTRY_CHART_DISPLAY_LIMIT);
        const dataset = buildIndustriesDataset(chartIndustries);
        setIndustriesData(dataset);
        setRawIndustries(sortedIndustries);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load industries distribution:', error);
        if (isMounted) {
          setIndustriesError('Failed to load industries data.');
          setRawIndustries([]);
        }
      } finally {
        if (isMounted) {
          setIndustriesLoading(false);
        }
      }
    };

    fetchTopTopics();
    fetchWordCloudTopics();
    fetchDisciplines();
    fetchIndustries();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  const barChartData = useMemo(
    () => ({
      labels: topTopicsData.labels,
      datasets: [
        {
          label: 'Messages',
          data: topTopicsData.values,
          backgroundColor: topTopicsData.labels.map((label) =>
            label.toLowerCase() === 'others' ? TOPIC_NEUTRAL_COLOR : TOPIC_BAR_COLOR
          ),
          borderRadius: 8,
          barThickness: 16,
        },
      ],
    }),
    [topTopicsData]
  );

  const disciplinesChartData = useMemo(
    () => ({
      labels: disciplinesData.labels,
      datasets: [
        {
          label: 'Messages',
          data: disciplinesData.values,
          backgroundColor: disciplinesData.labels.map(() => TOPIC_BAR_COLOR),
          borderRadius: 8,
          barThickness: 16,
        },
      ],
    }),
    [disciplinesData]
  );

  const barChartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = typeof context.parsed === 'number'
                ? context.parsed
                : context.parsed?.x;
              return `${value ?? 0} messages`;
            },
          },
        },
      },
      layout: {
        padding: {
          top: 12,
          bottom: 12,
        },
      },
      scales: {
        x: {
          ticks: { color: '#64748b' },
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
          beginAtZero: true,
        },
        y: {
          ticks: { color: '#64748b' },
          grid: { display: false },
        },
      },
    }),
    []
  );

  const industriesChartData = useMemo(
    () => ({
      labels: industriesData.labels,
      datasets: [
        {
          label: 'Messages',
          data: industriesData.values,
          backgroundColor: industriesData.labels.map(() => TOPIC_BAR_COLOR),
          borderRadius: 8,
          barThickness: 16,
        },
      ],
    }),
    [industriesData]
  );

  const buildCsvFilename = useCallback(
    (prefix: string) => {
      const startLabel = (dateRange.startDate ?? 'start').replace(/:/g, '-');
      const endLabel = (dateRange.endDate ?? 'end').replace(/:/g, '-');
      return `${prefix}_${startLabel}_to_${endLabel}.csv`;
    },
    [dateRange.endDate, dateRange.startDate]
  );

  const filteredWordCloudTopics = useMemo(
    () =>
      wordCloudTopicsData.labels
        .map((label, idx) => ({
          label,
          value: wordCloudTopicsData.values[idx] ?? 0,
        }))
        .filter(
          (topic) => topic.label.toLowerCase() !== 'others' && (topic.value ?? 0) > 0
        ),
    [wordCloudTopicsData]
  );

  // Measure word cloud container dimensions once when data is ready
  useEffect(() => {
    if (!wordCloudLoading &&
        !wordCloudError &&
        wordCloudTopicsData.labels.length > 0 &&
        !wordCloudDimensions &&
        wordCloudContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has been painted
      requestAnimationFrame(() => {
        if (wordCloudContainerRef.current) {
          const { width, height } = wordCloudContainerRef.current.getBoundingClientRect();
          if (width > 0 && height > 0) {
            setWordCloudDimensions({ width, height });
          }
        }
      });
    }
  }, [wordCloudLoading, wordCloudError, wordCloudTopicsData.labels.length, wordCloudDimensions]);

  const wordCloudData = useMemo<ChartData<'wordCloud'>>(() => {
    const labels = filteredWordCloudTopics.map((topic) => topic.label);
    const values = filteredWordCloudTopics.map((topic) => topic.value);

    const maxValue = Math.max(...values, 1);
    const normalizedWeights = values.map((value) => {
      if ((value ?? 0) <= 0) return WORD_CLOUD_WEIGHT_MIN;
      const ratio = value / maxValue;
      return Math.round(
        WORD_CLOUD_WEIGHT_MIN +
          ratio * (WORD_CLOUD_WEIGHT_MAX - WORD_CLOUD_WEIGHT_MIN)
      );
    });

    return {
      labels,
          datasets: [
            {
              label: 'Topic Mentions',
              data: normalizedWeights,
          color: () => TOPIC_BAR_COLOR,
          minRotation: 0,
          maxRotation: 0,
          rotationSteps: 1,
          spiral: 'rectangular',
          padding: 2,
          maxPadding: 4,
          shrinkToFit: true,
        },
      ],
    };
  }, [filteredWordCloudTopics]);

  const wordCloudOptions = useMemo<ChartOptions<'wordCloud'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => items[0]?.label ?? '',
            label: (context: TooltipItem<'wordCloud'>) => {
              const label =
                (wordCloudData.labels?.[context.dataIndex] as string | undefined) ?? '';
              const originalValue =
                filteredWordCloudTopics[context.dataIndex]?.value ?? 0;
              return `${label}: ${originalValue} messages`;
            },
          },
        },
      },
      layout: {
        padding: {
          top: 10,
          right: 10,
          bottom: 10,
          left: 10,
        },
      },
      elements: {
        word: {
          padding: 2,
          rotate: () => 0,
          minFontSize: WORD_CLOUD_FONT_MIN,
          maxFontSize: WORD_CLOUD_FONT_MAX,
          clip: true,
        },
      },
    }),
    [filteredWordCloudTopics, wordCloudData.labels]
  );

  const canExportTopTopics = Boolean(rawTopTopics?.length);
  const handleExportTopTopics = useCallback(() => {
    if (!rawTopTopics || !rawTopTopics.length) {
      return;
    }
    const spec = buildTopicsCsvSpec(rawTopTopics);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('top_topics'));
  }, [rawTopTopics, buildCsvFilename]);
  const topTopicsActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton size="small" onClick={handleExportTopTopics} disabled={!canExportTopTopics}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportTopTopics, handleExportTopTopics]
  );

  const canExportDisciplines = rawDisciplines.length > 0;
  const handleExportDisciplines = useCallback(() => {
    if (!rawDisciplines.length) {
      return;
    }
    const spec = buildDisciplinesCsvSpec(rawDisciplines);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('disciplines'));
  }, [rawDisciplines, buildCsvFilename]);
  const disciplinesActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportDisciplines}
            disabled={!canExportDisciplines}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportDisciplines, handleExportDisciplines]
  );

  const canExportIndustries = rawIndustries.length > 0;
  const handleExportIndustries = useCallback(() => {
    if (!rawIndustries.length) {
      return;
    }
    const spec = buildIndustriesCsvSpec(rawIndustries);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('industries'));
  }, [rawIndustries, buildCsvFilename]);
  const industriesActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportIndustries}
            disabled={!canExportIndustries}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportIndustries, handleExportIndustries]
  );

  const canExportWordCloud = rawWordCloudTopics.length > 0;
  const handleExportWordCloud = useCallback(() => {
    if (!rawWordCloudTopics.length) {
      return;
    }
    const spec = buildWordCloudCsvSpec(rawWordCloudTopics);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('word_cloud_topics'));
  }, [rawWordCloudTopics, buildCsvFilename]);
  const wordCloudActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportWordCloud}
            disabled={!canExportWordCloud}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportWordCloud, handleExportWordCloud]
  );

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setIsSearchDialogOpen(false);
      setSearchKeyword('');
      return;
    }
    setSearchKeyword(trimmed);
    setIsSearchDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Topics</h2>
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <DatePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={onDateRangeChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSearch();
              }
            }}
            placeholder="Search text in questions and answers..."
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <button
            type="button"
            onClick={handleSearch}
            className="flex w-full items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
          >
            Search
          </button>
        </div>

        <div className="grid flex-1 min-h-0 grid-cols-1 gap-6 lg:grid-cols-2 auto-rows-fr">
          <ChartCard
            title="Top Topics"
            fullHeight
            actions={topTopicsActions}
            tooltip={DASHBOARD_CARD_TOOLTIPS.topTopics}
          >
            {topTopicsLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading topics...
              </div>
            )}
            {!topTopicsLoading && topTopicsError && (
              <div className="flex h-full items-center justify-center text-sm text-red-500">
                {topTopicsError}
              </div>
            )}
            {!topTopicsLoading && !topTopicsError && topTopicsData.labels.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No data for the selected range.
              </div>
            )}
            {!topTopicsLoading && !topTopicsError && topTopicsData.labels.length > 0 && (
              <Bar data={barChartData} options={barChartOptions} />
            )}
          </ChartCard>
          <ChartCard
            title="Disciplines Ranking"
            fullHeight
            actions={disciplinesActions}
            tooltip={DASHBOARD_CARD_TOOLTIPS.disciplines}
          >
            {disciplinesLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading disciplines...
              </div>
            )}
            {!disciplinesLoading && disciplinesError && (
              <div className="flex h-full items-center justify-center text-sm text-red-500">
                {disciplinesError}
              </div>
            )}
            {!disciplinesLoading && !disciplinesError && disciplinesData.labels.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No data for the selected range.
              </div>
            )}
            {!disciplinesLoading && !disciplinesError && disciplinesData.labels.length > 0 && (
              <Bar data={disciplinesChartData} options={barChartOptions} />
            )}
          </ChartCard>
          <ChartCard
            title="Industries Ranking"
            fullHeight
            actions={industriesActions}
            tooltip={DASHBOARD_CARD_TOOLTIPS.industries}
          >
            {industriesLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading industries...
              </div>
            )}
            {!industriesLoading && industriesError && (
              <div className="flex h-full items-center justify-center text-sm text-red-500">
                {industriesError}
              </div>
            )}
            {!industriesLoading && !industriesError && industriesData.labels.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No data for the selected range.
              </div>
            )}
            {!industriesLoading && !industriesError && industriesData.labels.length > 0 && (
              <Bar data={industriesChartData} options={barChartOptions} />
            )}
          </ChartCard>
          <ChartCard
            title="Word Cloud"
            fullHeight
            actions={wordCloudActions}
            tooltip={DASHBOARD_CARD_TOOLTIPS.wordCloud}
          >
            <div ref={wordCloudContainerRef} className="w-full h-full">
              {wordCloudLoading && (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  Loading topics...
                </div>
              )}
              {!wordCloudLoading && wordCloudError && (
                <div className="flex h-full items-center justify-center text-sm text-red-500">
                  {wordCloudError}
                </div>
              )}
              {!wordCloudLoading && !wordCloudError && wordCloudTopicsData.labels.length === 0 && (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  No data for the selected range.
                </div>
              )}
              {!wordCloudLoading && !wordCloudError && wordCloudTopicsData.labels.length > 0 && wordCloudDimensions && (
                <div style={{ width: wordCloudDimensions.width, height: wordCloudDimensions.height, overflow: 'hidden' }}>
                <WordCloudErrorBoundary>
                <Chart type="wordCloud" data={wordCloudData} options={wordCloudOptions} />
              </WordCloudErrorBoundary>
                </div>
              )}
            </div>
          </ChartCard>
        </div>
      </div>
      <SearchDialog
        open={isSearchDialogOpen}
        onClose={() => setIsSearchDialogOpen(false)}
        keyword={searchKeyword}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
      />
    </div>
  );
};

export default TopicsTab;